/**
 * Zod schema for a single bulk-import CSV/XLSX row (EMP-10, Plan 03-09 +
 * be-029 D-20 alignment).
 *
 * Validates every column of a parsed row against the backend bulk-import
 * parser contract (`src/main/java/jo/linkco/hr/employees/internal/bulkimport/
 * CsvBulkImportParser.java` + `ArabicHeaderMap.java`). Field names match the
 * 29 canonical machine names exactly — the backend reads CSV column headers
 * directly and calls `row.get("first_name_ar")` etc.; mismatched names are
 * silently dropped.
 *
 * Per project conventions §Testing Philosophy — this schema is MUST-test tier.
 * Silent-breakage-expensive: a wrong parse at bulk-import scale ships
 * corrupted employee records into the tenant's roster.
 *
 * **Required vs. optional** mirrors backend `@NotBlank` / `@NotNull` / FK
 * constraints from be-029:
 *
 *   Required:   first_name_ar, father_name_ar, grandfather_name_ar,
 *               family_name_ar, nationality, document_type, hire_date,
 *               branch_code, contract_type_code
 *   Optional:   English name parts, national_id, dob, gender,
 *               marital_status, personal_phone/email, addresses,
 *               org_unit_code, position_code, manager_employee_code,
 *               function_code (parsed but not wired in v1), salary, bank.
 *
 * Document-type validation is driven by the tenant jurisdiction's served
 * identity-document catalog, not by a hardcoded code list: the accepted codes
 * and each code's number format are whatever the country issues, so a Saudi
 * tenant imports IQAMA / SAUDI_NID rows and a Jordanian one imports JNID /
 * RESIDENCY_PERMIT. Callers build the schema with `makeBulkImportRowSchema`,
 * handing it the catalog they already fetched. With no catalog (still loading,
 * or the lookup failed) document validation falls soft and the server decides.
 *
 * The Phase-5 reserved `function_code` column is parsed but discarded server-
 * side per be-029. We accept + strip it here so users mapping the column
 * don't get a "stripped unknown key" error in the preview.
 */

import { z } from "zod";
import {
  compileDocumentTypeValidators,
  type DocumentTypeCatalogEntry,
} from "../id-validators/documentTypeCatalog";
import { isoToDate } from "../dates/isoDate";
import { normalizeDigits } from "../format/normalizeDigits";
import { dateYearProblem } from "./textDates";

// ─── shared regexes ───────────────────────────────────────────────────────

/**
 * Shape only. It accepts `2026-02-30`, `2026-13-01` and `2026-00-10`, which is
 * why every date column pairs it with the `isoToDate` refinement below —
 * `Date` treats out-of-range components as arithmetic, so an impossible date
 * ships to the backend and either gets rejected after the preview promised it
 * wouldn't, or silently rolls over to 2026-03-02. `isoToDate` reads the
 * components back off the constructed `Date` for exactly this reason.
 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True when `value` names a day that exists on the calendar. */
const isRealCalendarDate = (value: string): boolean => isoToDate(value) !== undefined;
/** True when the year belongs on an employee record (see `dateYearProblem`). */
const isPlausibleYear = (value: string): boolean => dateYearProblem(value) === null;
const ISO_COUNTRY_RE = /^[A-Z]{2}$/; // ISO 3166-1 alpha-2

const NAME_MAX = 80;
const ADDRESS_MAX = 2000;

/** How the server reads a word cell: case and spacing don't matter. */
const cellKey = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, " ");

/** The religion words the server accepts, in either language, plus the values themselves. */
const RELIGION_WORDS: ReadonlySet<string> = new Set([
  "muslim",
  "islam",
  "مسلم",
  "مسلمة",
  "مسلمه",
  "الإسلام",
  "الاسلام",
  "إسلام",
  "اسلام",
  "christian",
  "christianity",
  "مسيحي",
  "مسيحية",
  "مسيحيه",
  "المسيحية",
  "المسيحيه",
  "other",
  "أخرى",
  "اخرى",
  "آخر",
  "اخر",
  "غير ذلك",
]);

/** A yes/no cell, in either language, as the server reads it. */
const YES_NO_WORDS: ReadonlySet<string> = new Set([
  "yes",
  "y",
  "true",
  "1",
  "نعم",
  "no",
  "n",
  "false",
  "0",
  "لا",
]);

/** Several countries in one cell, split where the server splits them. */
const splitCountries = (value: string): string[] =>
  value
    .split(/[,;\u060C]/)
    .map((part) => part.trim())
    .filter((part) => part !== "");

const optionalIsoDate = (field: string) =>
  z
    .string()
    .regex(ISO_DATE_RE, `${field} must be YYYY-MM-DD`)
    .refine(isRealCalendarDate, `${field} must be a real calendar date`)
    .refine(isPlausibleYear, `${field} must be a Gregorian date between 1900 and 2100`)
    .optional();

// ─── base row shape — 29 D-20 columns ─────────────────────────────────────

/**
 * Base shape — exactly mirrors the 29 canonical machine names from be-029.
 * Unknown keys are stripped by Zod's default `.strip()` behavior. The
 * Phase-5 reserved `function_code` column is read + discarded server-side
 * (we accept it as optional here so the preview doesn't reject rows that
 * include it).
 */
const baseRowSchema = z.object({
  // Arabic name (4 required parts) — backend @NotBlank
  first_name_ar: z.string().min(1).max(NAME_MAX),
  father_name_ar: z.string().min(1).max(NAME_MAX),
  grandfather_name_ar: z.string().min(1).max(NAME_MAX),
  family_name_ar: z.string().min(1).max(NAME_MAX),

  // English name (4 optional parts)
  first_name_en: z.string().max(NAME_MAX).optional(),
  middle_name_en: z.string().max(NAME_MAX).optional(),
  last_name_en: z.string().max(NAME_MAX).optional(),
  second_last_name_en: z.string().max(NAME_MAX).optional(),

  // Citizenship + identity
  nationality: z.string().regex(ISO_COUNTRY_RE, "nationality must be ISO 3166-1 alpha-2 (e.g. JO)"),
  // Accepted values come from the jurisdiction's catalog, checked below.
  document_type: z.string().min(1, "document_type is required"),
  national_id: z.string().max(40).optional(),

  // Personal
  date_of_birth: z
    .string()
    .regex(ISO_DATE_RE, "date_of_birth must be YYYY-MM-DD")
    .refine(isRealCalendarDate, "date_of_birth must be a real calendar date")
    .refine(isPlausibleYear, "date_of_birth must be a Gregorian date between 1900 and 2100")
    .optional(),
  // CC-443: bulk import accepts a NARROWER set than the enums do. `OTHER` and
  // `PREFER_NOT_TO_SAY` stay valid on the profile screens and on existing
  // records — a spreadsheet column offering four spellings of one question
  // produces inconsistent rosters, and gender feeds gender-restricted leave
  // entitlement. Both cells remain OPTIONAL: this constrains what may go in
  // them, not whether they must be filled.
  gender: z.enum(["MALE", "FEMALE"]).optional(),
  marital_status: z.enum(["SINGLE", "MARRIED", "DIVORCED", "WIDOWED"]).optional(),
  personal_phone: z.string().max(20).optional(),
  personal_email: z.string().email("personal_email must be a valid email").max(120).optional(),
  // The address a portal invitation is sent to when the importer asks for it.
  work_email: z.string().email("work_email must be a valid email").max(120).optional(),
  address_ar: z.string().max(ADDRESS_MAX).optional(),
  address_en: z.string().max(ADDRESS_MAX).optional(),

  // Employment
  hire_date: z
    .string()
    .regex(ISO_DATE_RE, "hire_date must be YYYY-MM-DD")
    .refine(isRealCalendarDate, "hire_date must be a real calendar date")
    .refine(isPlausibleYear, "hire_date must be a Gregorian date between 1900 and 2100"),
  branch_code: z.string().min(1, "branch_code is required"),
  org_unit_code: z.string().optional(),
  position_code: z.string().optional(),
  contract_type_code: z.string().min(1, "contract_type_code is required"),
  manager_employee_code: z.string().optional(),
  function_code: z.string().optional(), // reserved per be-029

  // Compensation (optional in v1)
  base_salary: z.coerce.number().nonnegative().optional(),
  bank_name: z.string().max(120).optional(),
  bank_branch: z.string().max(120).optional(),
  // The one bank column the import carries. It is the plain account column of
  // the employee record (varchar(40)), and holds an IBAN wherever payroll needs
  // one — the separate `iban` field is HTTP-only and has no import column, so
  // an `IBAN` header maps here. Format is left to the server, which reports a
  // bad account per row when the bank file is generated rather than refusing
  // the whole roster at import.
  bank_account_number: z.string().max(40).optional(),

  // Per-employee extra annual tax exemption, on top of the statutory bands.
  extra_tax_exemption_annual: z.coerce.number().nonnegative().optional(),

  // Optional manual employee code (CC-177) — charset mirrors the backend's
  // ^[A-Za-z0-9][A-Za-z0-9._/-]*$ (max 64). Blank/absent → backend
  // gives the next plain number. (Empty cells are normalized to undefined
  // before validation, same as personal_email's strict optional pattern.)
  employee_code: z
    .string()
    .max(64, "employee_code must be 64 characters or fewer")
    .regex(
      /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
      "employee_code may use letters, numbers, and . _ / - (start with a letter or number)",
    )
    .optional(),

  // Optional record fields, with the server's length limits.
  payslip_name_en: z.string().max(120).optional(),
  payslip_name_ar: z.string().max(120).optional(),
  additional_nationalities: z
    .string()
    .refine((value) => {
      const codes = splitCountries(value);
      return (
        codes.length <= 3 &&
        codes.every((code) => ISO_COUNTRY_RE.test(code)) &&
        new Set(codes).size === codes.length
      );
    }, "additional_nationalities must be up to 3 different ISO 3166-1 alpha-2 codes, comma-separated")
    .optional(),
  home_country: z
    .string()
    .regex(ISO_COUNTRY_RE, "home_country must be ISO 3166-1 alpha-2 (e.g. JO)")
    .optional(),
  birth_country: z
    .string()
    .regex(ISO_COUNTRY_RE, "birth_country must be ISO 3166-1 alpha-2 (e.g. JO)")
    .optional(),
  probation_end_date: optionalIsoDate("probation_end_date"),
  religion: z
    .string()
    .refine(
      (value) => RELIGION_WORDS.has(cellKey(value)),
      "religion must be Muslim, Christian or Other",
    )
    .optional(),
  sponsor_name: z.string().max(120).optional(),
  sponsor_id: z.string().max(40).optional(),
  labor_card_number: z.string().max(40).optional(),
  labor_card_expiry: optionalIsoDate("labor_card_expiry"),
  mol_number: z.string().max(40).optional(),
  outsourced: z
    .string()
    .refine((value) => YES_NO_WORDS.has(cellKey(value)), "outsourced must be yes or no")
    .optional(),
  // Resolved by the server like manager_employee_code.
  supervisor_employee_code: z.string().optional(),
  gl_account_number: z.string().max(40).optional(),
  integration_id: z.string().max(64).optional(),
});

/** Parsed + validated bulk-import row (output type after coercions). */
export type BulkImportRow = z.infer<typeof baseRowSchema>;

/**
 * Rows of one file that carry the same identity number after trimming and
 * folding Arabic-Indic digits, whatever their document type: the server's
 * uniqueness check ignores the type too. Each row is valid on its own, and the
 * server only notices on a real run, so the preview has to compare rows with
 * each other. Returns groups of row indexes (2+ each).
 */
export function duplicateNationalIdGroups(
  rows: ReadonlyArray<{ document_type?: string | undefined; national_id?: string | undefined }>,
): number[][] {
  const byKey = new Map<string, number[]>();
  rows.forEach((row, idx) => {
    const id = normalizeDigits(row.national_id ?? "").trim();
    if (id === "") return;
    byKey.set(id, [...(byKey.get(id) ?? []), idx]);
  });
  return [...byKey.values()].filter((group) => group.length > 1);
}

/**
 * Build the row schema for a tenant's jurisdiction. `documentTypes` is the
 * served identity-document catalog — the same list the identity pickers use —
 * and it is the only thing that decides which `document_type` values import
 * and what each one's `national_id` may look like. Build once per catalog
 * response, not once per row.
 */
export function makeBulkImportRowSchema(
  documentTypes: readonly DocumentTypeCatalogEntry[],
): z.ZodEffects<typeof baseRowSchema> {
  const validators = compileDocumentTypeValidators(documentTypes);
  const labels = new Map(documentTypes.map((d) => [d.code, d.labelEn || d.code]));
  return baseRowSchema.superRefine((val, ctx) => {
    // The server refuses the main nationality among the others.
    if (
      val.additional_nationalities !== undefined &&
      splitCountries(val.additional_nationalities).includes(val.nationality)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["additional_nationalities"],
        message: "additional_nationalities must not repeat the nationality",
      });
    }
    // No catalog (still loading, or the lookup failed) — fall soft rather than
    // reject rows the server would accept.
    if (validators.size === 0) return;
    const validate = validators.get(val.document_type);
    if (validate === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["document_type"],
        message: `document_type must be one of: ${[...validators.keys()].join(", ")}`,
      });
      return;
    }
    // Format check only when a number is present — it stays optional.
    if (val.national_id !== undefined && val.national_id.length > 0 && !validate(val.national_id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["national_id"],
        message: `${labels.get(val.document_type) ?? val.document_type} number is not in the expected format`,
      });
    }
  });
}
